
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WaveformDisplayProps {
  audioUrl: string;
  color?: string;
  index?: number;
  name: string;
}

const stemColors = [
  '#50A090', // Increased saturation Green
  '#66C6BF', // Increased saturation Teal/Green-Blue
  '#5D9AC8', // Increased saturation Blue
  '#4A74A0', // Increased saturation Dark Blue
  '#68669A', // Increased saturation Purple
  '#A074AA', // Increased saturation Magenta
  '#C28EBA', // Increased saturation Pink
];

const WaveformDisplay = ({ audioUrl, index = 0, name }: WaveformDisplayProps) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<string>('00:00');
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);

  const color = stemColors[index % stemColors.length];
  const backgroundColor = `${color}40`; // 25% opacity version of the color

  useEffect(() => {
    let isMounted = true;

    const initializeWaveSurfer = async () => {
      if (!waveformRef.current || !isMounted) return;

      // Cleanup previous instance
      if (wavesurfer.current) {
        wavesurfer.current.destroy();
        wavesurfer.current = null;
      }

      setIsLoading(true);
      setLoadProgress(0);

      try {
        const container = waveformRef.current;
        container.style.backgroundColor = backgroundColor;

        const ws = WaveSurfer.create({
          container,
          waveColor: color,
          progressColor: color,
          cursorColor: color,
          barWidth: 1,
          barGap: 1,
          height: 64,
          normalize: false,
          barRadius: 1,
        });

        wavesurfer.current = ws;

        ws.on('loading', (progress) => {
          if (isMounted) {
            console.log('Loading progress:', progress);
            setLoadProgress(progress);
          }
        });

        ws.on('ready', () => {
          if (isMounted) {
            console.log('Waveform ready');
            setIsLoading(false);
            const totalSeconds = Math.floor(ws.getDuration() || 0);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            setDuration(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
          }
        });

        ws.on('play', () => isMounted && setIsPlaying(true));
        ws.on('pause', () => isMounted && setIsPlaying(false));
        ws.on('finish', () => isMounted && setIsPlaying(false));

        await ws.load(audioUrl);
      } catch (error) {
        console.error('Error initializing WaveSurfer:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeWaveSurfer();

    return () => {
      isMounted = false;
      if (wavesurfer.current) {
        wavesurfer.current.destroy();
        wavesurfer.current = null;
      }
    };
  }, [audioUrl, color, backgroundColor]);

  const togglePlayPause = () => {
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
    }
  };

  return (
    <div className="flex-1 flex items-center gap-4 h-full">
      <div className="w-48 flex-shrink-0 flex items-center gap-2 px-2">
        <Button
          variant="outline"
          size="icon"
          onClick={togglePlayPause}
          className="bg-transparent border border-white/15 hover:bg-white/5 w-10 h-10 flex-shrink-0"
          disabled={isLoading}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </Button>
        <span className="text-sm text-gray-400 truncate flex-1">{name}</span>
      </div>
      <div className="flex-1 h-full relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="w-full bg-gray-700/20 rounded-lg overflow-hidden">
              <div 
                className="h-16 bg-current opacity-10 transition-all duration-300 ease-out rounded-lg"
                style={{ 
                  width: `${loadProgress}%`,
                  backgroundColor: color
                }}
              />
            </div>
          </div>
        )}
        <div ref={waveformRef} className="h-full rounded-lg" />
      </div>
      <span className="text-sm text-gray-400 min-w-[48px] my-auto">
        {isLoading ? '--:--' : duration}
      </span>
    </div>
  );
};

export default WaveformDisplay;
